// @dynamic-i18n-keys
// Dynamic t() at line 45 (renderStat stat: 'deviations' | 'posts'):
//   profileStats.deviations
//   profileStats.posts
import type { FC } from 'react';
import { useMemo, useEffect } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import uniqueId from 'lodash/uniqueId';
import {
  PopperDropdownManager,
  PopperDropdownToggle,
} from '@wix/da-ds/pkg/Dropdown/PopperDropdownLayout';
import { useDropdownItems } from '@wix/da-ds/pkg/Dropdown/hooks/useDropdownItems';
import StandardDropdownMenu from '@wix/da-ds/pkg/Dropdown/Preset/StandardDropdown/StandardDropdownMenu';
import { NullableDropdownItem } from '@wix/da-ds/pkg/Dropdown/types';
import ArrowDownIcon from '@wix/da-ds/pkg/Icons/24x24/ArrowDown';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { getDeviationEntityId } from '@wix/da-shared-react/pkg/redux/normalizr/schemas/deviation';

import s from './TiersDropdown.scss';

export interface Props {
  className?: string;
  tiers: PapiDeviation[];
  selectedTier?: PapiDeviation;
  setSelectedTierEntityId: (tierId: string | number) => void;
}

const TiersDropdown: FC<Props> = ({ className, tiers, selectedTier, setSelectedTierEntityId }) => {
  const id = uniqueId('tiersdropdown');
  const { t } = useTranslation();
  const items: NullableDropdownItem[] = useMemo(
    () =>
      tiers.map(tier => ({
        label: tier.title,
        value: getDeviationEntityId(tier),
      })),
    [tiers]
  );
  const { filteredItems } = useDropdownItems(items);
  const renderStat = (stat, value) =>
    t(`profileStats.${stat}`, {
      postProcess: ['reactPostprocessor', 'featureBrancher'],
      count: value,
      counter: value,
      stat,
    });

  useEffect(() => {
    if (!selectedTier && tiers.length > 0) {
      setSelectedTierEntityId(getDeviationEntityId(tiers[0]));
    }
  }, [selectedTier, tiers, setSelectedTierEntityId]);

  const statDeviations = selectedTier?.tier?.stats?.deviations;
  const statPosts = selectedTier?.tier?.stats?.posts;

  return (
    <div className={classnames(s['root'], className)}>
      <PopperDropdownManager isInPopup disabled={tiers.length === 1}>
        <PopperDropdownToggle className={s['toggle']}>
          <span>{selectedTier?.title}</span>
          {tiers.length > 1 && <ArrowDownIcon className={s['caret']} size={IconSize.MEDIUM} />}
        </PopperDropdownToggle>
        <StandardDropdownMenu
          id={id}
          role={'menu'}
          placement="bottom-start"
          className={s['menu']}
          items={filteredItems}
          onSelect={setSelectedTierEntityId}
          menuScrollable
          allowFlip={false}
        />
      </PopperDropdownManager>
      <div className={s['count']}>
        {!!statDeviations && renderStat('deviations', statDeviations)}
        {!!statDeviations && !!statPosts && ' | '}
        {!!statPosts && renderStat('posts', statPosts)}
      </div>
    </div>
  );
};
TiersDropdown.displayName = 'TiersDropdown';

export default TiersDropdown;
